import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface UseGoogleAnalyticsProps {
  trackingId?: string;
  debug?: boolean;
}

export const useGoogleAnalytics = ({
  trackingId,
  debug = false
}: UseGoogleAnalyticsProps = {}) => {
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [savedTrackingId, setSavedTrackingId] = useState<string | null>(
    localStorage.getItem('ga_tracking_id') || null
  );

  // Initialize Google Analytics
  const initGA = (id: string) => {
    if (!id) return;
    
    // Save tracking ID to localStorage
    localStorage.setItem('ga_tracking_id', id);
    setSavedTrackingId(id);
    
    // Create script elements for Google Analytics
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${id}', { 'send_page_view': false });
    `;
    
    // Add scripts to document
    document.head.appendChild(gtagScript);
    document.head.appendChild(inlineScript);
    
    setIsInitialized(true);
    
    if (debug) {
      console.log('Google Analytics initialized with ID:', id);
    }
  };

  // Send page view
  const pageView = (path: string) => {
    if (!isInitialized || !window.gtag) return;
    
    window.gtag('config', savedTrackingId || trackingId || '', {
      page_path: path
    });
    
    if (debug) {
      console.log('Page view sent:', path);
    }
  };

  // Track event
  const event = (action: string, category: string, label: string, value: number = 0) => {
    if (!isInitialized || !window.gtag) return;
    
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });
    
    if (debug) {
      console.log('Event tracked:', { action, category, label, value });
    }
  };

  // Set tracking ID
  const setTrackingId = (id: string) => {
    if (id === savedTrackingId) return;
    
    // Remove existing GA scripts
    document.querySelectorAll('script[src*="googletagmanager.com"]').forEach(script => {
      script.remove();
    });
    
    // Initialize with new ID
    initGA(id);
  };

  // Initialize on component mount
  useEffect(() => {
    if (trackingId) {
      initGA(trackingId);
    } else if (savedTrackingId) {
      initGA(savedTrackingId);
    }
  }, [trackingId]);

  // Track page views
  useEffect(() => {
    if (isInitialized) {
      pageView(location.pathname + location.search);
    }
  }, [location, isInitialized]);

  return {
    isInitialized,
    trackingId: savedTrackingId || trackingId,
    setTrackingId,
    pageView,
    event
  };
};

// Add window.gtag type
declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
    dataLayer: any[];
  }
}